/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FORM_CONTROL_IMPORTS} from './form-control-imports';

/**
 * Renders a list of checkboxes bound to a mutable string array (the selection).
 *
 * Simple usage (items are plain strings, optional translate prefix):
 *   <adn-checklist-array [items]="['EMAIL','UI']"
 *                  [selectedValues]="myArray"
 *                  translatePrefix="deliveryMethod."
 *                  (changed)="markExternalFormDirty()">
 *   </adn-checklist-array>
 *
 * Object usage (items are objects, specify which property is the stored value and which is the label):
 *   <adn-checklist-array [items]="allTeams"
 *                  valueKey="id"
 *                  labelKey="name"
 *                  [selectedValues]="editingModel.teams"
 *                  (changed)="markExternalFormDirty()">
 *   </adn-checklist-array>
 *
 * Optional help text under each label (object property via helpKey):
 *   <adn-checklist-array [items]="allLayouts"
 *                  valueKey="id"
 *                  labelKey="name"
 *                  helpKey="categoryHelp"
 *                  ...>
 */
@Component({
  standalone: true,
  imports: FORM_CONTROL_IMPORTS,
  selector: 'adn-checklist-array',
  template: `
    <div [class.row]="columns > 1">
      <div *ngFor="let item of items" [class.col-md-6]="columns === 2" [class.col-md-4]="columns === 3" [style.margin-bottom.px]="helpKey ? 2 : null">
        <label class="checkbox-inline" [style.margin-bottom.px]="helpKey ? 0 : null">
          <input type="checkbox"
                 [disabled]="disabled"
                 [checked]="isSelected(item)"
                 (change)="toggle(item)">
          <ng-container *ngIf="labelKey">{{ getLabel(item) }}</ng-container>
          <ng-container *ngIf="!labelKey && translatePrefix">{{ (translatePrefix + getValue(item)) | ogTranslate }}</ng-container>
          <ng-container *ngIf="!labelKey && !translatePrefix">{{ getValue(item) }}</ng-container>
        </label>
        <div *ngIf="helpKey && getHelp(item)" class="help-block small" style="margin: 0 0 0 20px; line-height: 1.2;">{{ getHelp(item) }}</div>
      </div>
    </div>
  `
})
export class AdnChecklistComponent {
  /** The list of options to display. Can be strings or objects. */
  @Input() items: any[] = [];

  /**
   * The mutable array of currently selected values. The component writes
   * directly into this array (splice / push), so the parent's reference stays
   * stable — no need for two-way binding.
   */
  @Input() selectedValues: string[] = [];

  /** If items are objects, which property holds the stored value (e.g. 'id'). */
  @Input() valueKey?: string;

  /** If items are objects, which property to display as the label (e.g. 'name'). */
  @Input() labelKey?: string;

  /** If items are objects, optional property for small help text under the label. */
  @Input() helpKey?: string;

  /**
   * Optional i18n prefix applied to the value before piping through ogTranslate.
   * Only used when labelKey is not set. E.g. 'deliveryMethod.' → 'deliveryMethod.EMAIL'.
   */
  @Input() translatePrefix?: string;

  @Input() disabled = false;

  /** Number of Bootstrap columns to spread checklist items across (1 = stacked). */
  @Input() columns = 1;

  /** Emitted after every toggle so the parent can react (e.g. mark form dirty). */
  @Output() changed = new EventEmitter<void>();

  getValue(item: any): string {
    return this.valueKey ? item[this.valueKey] : item;
  }

  getLabel(item: any): string {
    return this.labelKey ? item[this.labelKey] : String(this.getValue(item));
  }

  getHelp(item: any): string {
    return this.helpKey ? (item[this.helpKey] || '') : '';
  }

  isSelected(item: any): boolean {
    const value = this.getValue(item);
    return this.selectedValues ? this.selectedValues.includes(value) : false;
  }

  toggle(item: any): void {
    if (!this.selectedValues) {
      return;
    }
    const value = this.getValue(item);
    const idx = this.selectedValues.indexOf(value);
    if (idx >= 0) {
      this.selectedValues.splice(idx, 1);
    } else {
      this.selectedValues.push(value);
    }
    this.changed.emit();
  }
}
